import React, { useEffect, useMemo, useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';

import { AlertList, ClickableIcon, ErrorResponse, Modal, Spinner } from '~components';
import { faArrowRightArrowLeft } from '~components/icon';
import { Form as WizardForm } from '~components/wizard-form';

import cutoverSteps, { CutoverResults } from '~support-at-home/forms/sah-associations/cutover';

import {
  CutoverPairOption,
  useApplyCutoverBudgetsMutation,
  useApplyCutoverPricePlansMutation,
  useApplyCutoverServiceTypesMutation,
  useGetActiveCutoverRunQuery,
} from '~support-at-home/actions/sahServiceCutover';

import CutoverRunProgress from './CutoverRunProgress';

/** The wizard step indexes that apply something when the provider confirms them. */
const SERVICE_TYPE_STEP = 1;
const PRICE_PLAN_STEP = 2;
const BUDGET_STEP = 3;

/** A first step stated for the provider rather than built by them. */
export interface CutoverPrefill {
  cutover_date: string;
  pairs: CutoverPairOption[];
}

interface ComponentProps {
  canUpdate: boolean;
  /** Called after any step has been applied, so the associations list behind the modal is re-read. */
  onApplied: () => void;
  /**
   * Opens the assistant with its first step already stated - what the mandate banner's action
   * supplies. Cleared through `onPrefillConsumed` once taken, so closing and re-opening by the
   * toolbar icon gives an empty assistant rather than the last prefill again.
   */
  prefill?: CutoverPrefill | null;
  onPrefillConsumed?: () => void;
}

/**
 * The Support at Home service cutover assistant.
 *
 * Started from the Service Type Assocs. toolbar, because that is where a provider goes to do the
 * first part of this work by hand today.
 *
 * Each step is applied as it is confirmed rather than everything being held to the end. That is what
 * `beforeStepChange` is doing here: the wizard asks whether it may advance, and the answer is "yes,
 * once this step's work is done". A step that fails to apply keeps the provider where they are with
 * the reason on screen, so the steps already applied stand and the one that did not is obvious.
 */
const ServiceCutover: React.FC<ComponentProps> = ({ canUpdate, onApplied, prefill = null, onPrefillConsumed }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  // A budget run outlives the request that starts it, so the modal stays locked until it finishes -
  // closing mid-run would abandon a provider's only view of work that is still changing their data.
  const [isRunning, setIsRunning] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [results, setResults] = useState<CutoverResults>({});

  // Read on open, not on mount: a provider whose browser was refreshed mid-run has no way back to
  // the progress otherwise, and cannot start another run either - the server refuses a second one
  // while the first is live, which would leave them stuck with no idea how far it had got.
  const { data: activeRun, isFetching: isCheckingForRun } = useGetActiveCutoverRunQuery(undefined, {
    skip: !isOpen,
    refetchOnMountOrArgChange: true,
  });

  // Only a run this session did not start. One it did is already being watched on the last step, and
  // showing the resume view instead would throw away the record of what the earlier steps did.
  const resumedRun = activeRun && activeRun.run_id !== results.budget_run_id ? activeRun : null;

  const [applyServiceTypes] = useApplyCutoverServiceTypesMutation();
  const [applyPricePlans] = useApplyCutoverPricePlansMutation();
  const [applyBudgets] = useApplyCutoverBudgetsMutation();

  const steps = useMemo(() => cutoverSteps({ ...results, onRunningChange: setIsRunning }), [results]);

  // A prefill both opens the assistant and states its first step. Held as the form's initial values
  // rather than pushed in afterwards: the wizard reads these once, so a provider who then edits the
  // pairs - removing one, adding another - is editing their own run, not fighting a value that keeps
  // being reasserted.
  useEffect(() => {
    if (prefill) setIsOpen(true);
  }, [prefill]);

  const initialValues = prefill ? { cutover_date: prefill.cutover_date, pairs: prefill.pairs } : undefined;

  // Nothing is held server-side between runs, so closing discards only the choices on screen - never
  // work already applied. State is reset here rather than on open so a re-opened assistant never
  // shows the previous run's summary.
  const close = () => {
    if (isApplying || isRunning) return;
    setIsOpen(false);
    setApplyError(null);
    setResults({});
    onPrefillConsumed?.();
  };

  const applyStep = async (currentStep: number, values: any): Promise<boolean> => {
    const context = {
      cutover_date: values?.cutover_date,
      // Stripped back to what the server needs - the builder carries service names on each pair so
      // its own table can render them, and those have no business in the request.
      pairs: (values?.pairs ?? []).map(({ from_service_id, to_service_id }: any) => ({
        from_service_id,
        to_service_id,
      })),
    };
    const service_type_ids = values?.service_type_ids ?? [];

    if (currentStep === SERVICE_TYPE_STEP) {
      const applied = await applyServiceTypes({ ...context, service_type_ids }).unwrap();
      setResults((previous) => ({ ...previous, applied_service_types: applied }));
      return true;
    }

    if (currentStep === PRICE_PLAN_STEP) {
      const applied = await applyPricePlans({
        ...context,
        service_type_ids,
        plan_ids: values?.plan_ids ?? [],
      }).unwrap();
      setResults((previous) => ({ ...previous, applied_price_plans: applied }));
      return true;
    }

    if (currentStep === BUDGET_STEP) {
      // Starts a background run and hands back its id. The work itself is watched on the next step -
      // two thousand budgets takes around forty seconds, far longer than a request should be held.
      const started = await applyBudgets({
        ...context,
        service_type_ids,
        status_ids: values?.status_ids ?? [],
        excluded_budget_ids: values?.excluded_budget_ids ?? [],
      }).unwrap();
      setResults((previous) => ({ ...previous, budget_run_id: started.run_id }));
      return true;
    }

    return true;
  };

  const handleBeforeStepChange = async (
    currentStep: number,
    values: any,
    direction: 'next' | 'prev',
  ): Promise<boolean> => {
    setApplyError(null);

    // Going back never writes. A step applies on the way FORWARD out of it, so without this a Back
    // button would apply the very step the provider is retreating from - the reason the wizard had
    // no Back at all until the shared form learned to say which way it was moving.
    if (direction === 'prev') return true;

    // The first step only collects the date and the replacements, and the last only reports what was
    // done - neither writes anything, so neither needs the lock or error handling.
    if (currentStep !== SERVICE_TYPE_STEP && currentStep !== PRICE_PLAN_STEP && currentStep !== BUDGET_STEP) {
      return true;
    }

    // Nothing to bring into line: there is no run to start, and the server refuses an empty one. Moving
    // on writes nothing, so it needs neither the lock nor onApplied.
    if (currentStep === BUDGET_STEP && values?.budgets_empty === true) {
      setResults((previous) => ({ ...previous, budgets_skipped: true }));
      return true;
    }

    setIsApplying(true);
    try {
      const applied = await applyStep(currentStep, values);
      if (applied) onApplied();
      return applied;
    } catch (error) {
      setApplyError(getErrorMessage(error));
      return false;
    } finally {
      setIsApplying(false);
    }
  };

  if (!canUpdate) return null;

  return (
    <>
      <ClickableIcon
        id="sah-service-cutover-button"
        icon={faArrowRightArrowLeft}
        onClick={() => setIsOpen(true)}
        toolTip="Service Cutover Assistant"
        // The tooltip is a separate portaled element and does not name the control to assistive
        // technology, so an icon-only role=button needs its own label or axe flags it as
        // aria-command-name. Same fix the search toggle beside this one carries.
        ariaLabel="Service Cutover Assistant"
      />
      <Modal
        size="xl"
        isOpen={isOpen}
        setIsOpen={(open: boolean) => (open ? setIsOpen(true) : close())}
        onClose={close}
        headerClassName="header"
        header="Support at Home Service Cutover"
        className="sah-service-cutover-wizard-modal"
        includeFooter={false}
        // A step commits as it is confirmed, so an apply in flight must not be abandoned half way -
        // the records it has already written would stand with nothing on screen saying so.
        backdrop={isApplying || isRunning ? 'static' : true}
        keyboard={!isApplying && !isRunning}
        disableClose={isApplying || isRunning}
        unmountOnClose
      >
        {isCheckingForRun && <Spinner loading />}

        {/* A run already in flight replaces the wizard rather than sitting inside it. The assistant
            holds nothing server-side between steps, so there is no earlier progress to restore - and
            there is nothing useful to do while a run is live anyway, since a second one is refused. */}
        {!isCheckingForRun && resumedRun && (
          <div>
            <AlertList
              header="A service cutover is already under way"
              color="info"
              listItems={[
                // Named where the account still resolves. A run is guarded per company, so this may
                // well be a colleague's rather than one of their own they have forgotten about.
                resumedRun.started_by
                  ? `Started by ${resumedRun.started_by}, and still going. It carries on whether or not this is open.`
                  : 'This was started earlier and is still going. It carries on whether or not this is open.',
                'Nothing else can be started until it finishes - start the assistant again once it is done to carry anything left behind.',
              ]}
            />
            <CutoverRunProgress runId={resumedRun.run_id} onRunningChange={setIsRunning} />
          </div>
        )}

        {!isCheckingForRun && !resumedRun && (
          <WizardForm
            steps={steps}
            initialValues={initialValues}
            beforeStepChange={handleBeforeStepChange}
            onComplete={close}
            submitCaption="Close"
            renderFormBody={(formBody) => (
              <>
                {applyError && <ErrorResponse message={applyError} />}
                {formBody}
              </>
            )}
          />
        )}
      </Modal>
    </>
  );
};

export default ServiceCutover;
